/**
 * The words in a cockpit, as one texture atlas.
 *
 * ## Why placards matter more than they sound like they should
 *
 * A cockpit is covered in writing. Every switch has a label above it, every lever has its travel
 * marked, and there are warnings screwed to the panel about things that will bend the aeroplane.
 * An interior without any of it reads as a *model* of a cockpit rather than a cockpit — the eye
 * does not need to read the words, it needs them to be there, because a real machine is annotated
 * by the people who have to operate it.
 *
 * ## One atlas, one draw call
 *
 * Every label in the aeroplane is drawn into a single canvas and every placard is a quad with UVs
 * into it. That means all of them merge into one mesh with one material, which is the same reason
 * the cabin is four meshes and not forty: CLAUDE.md allows 350 draw calls for the whole frame, and
 * a cockpit is not entitled to spend thirty of them on lettering.
 *
 * The atlas is generated at start-up rather than shipped, for the same reason the grain was before
 * the photographs arrived: it is text on a flat background, it costs about a millisecond, and a PNG
 * of it would be a download and a file to keep in sync with the labels this file already lists.
 *
 * ## Unlit, and deliberately
 *
 * Placards are engraved or silk-screened, and what makes them legible in a dark cockpit is that
 * they are *painted white on black* — not that they are lit. A `MeshBasicNodeMaterial` also keeps
 * them readable when the cabin around them goes to the flood light at dusk, which is precisely
 * when a pilot is looking for the switch labels.
 *
 * `updateFixed` / `updateFrame`: neither. Built once, cached for the session.
 */

import { CanvasTexture, LinearFilter, SRGBColorSpace } from 'three';

/** Atlas size. 512 gives each of the 64 cells 128×32, which is legible at arm's length. */
const ATLAS = 512;
const COLUMNS = 4;
const ROWS = 16;
const CELL_W = ATLAS / COLUMNS;
const CELL_H = ATLAS / ROWS;

/**
 * Every label the fleet uses, in atlas order.
 *
 * One list rather than one per aircraft: the atlas is shared, a cell costs nothing to leave unused,
 * and a jet that never shows `CARB HEAT` is cheaper than two textures. `style` decides the colour —
 * a warning placard is red on white and a switch label is white on black, and getting that the
 * wrong way round is the difference between a cockpit and a novelty.
 */
export type PlacardId =
  | 'master' | 'alt' | 'beacon' | 'nav' | 'strobe' | 'land' | 'pitot' | 'fuelPump'
  | 'carbHeat' | 'mixture' | 'throttle' | 'flaps' | 'gear' | 'trim' | 'breakers' | 'fuelSel'
  | 'noSmoking' | 'gearWarn'
  | 'masterArm' | 'gun' | 'missile' | 'jettison' | 'eject' | 'canopy' | 'radar' | 'ecm';

interface PlacardSpec {
  text: string;
  style: 'label' | 'warning' | 'caution';
}

const PLACARDS: Readonly<Record<PlacardId, PlacardSpec>> = Object.freeze({
  master: { text: 'MASTER', style: 'label' },
  alt: { text: 'ALT', style: 'label' },
  beacon: { text: 'BCN', style: 'label' },
  nav: { text: 'NAV', style: 'label' },
  strobe: { text: 'STROBE', style: 'label' },
  land: { text: 'LAND', style: 'label' },
  pitot: { text: 'PITOT HT', style: 'label' },
  fuelPump: { text: 'FUEL PUMP', style: 'label' },
  carbHeat: { text: 'CARB HEAT', style: 'label' },
  mixture: { text: 'MIXTURE', style: 'label' },
  throttle: { text: 'THROTTLE', style: 'label' },
  flaps: { text: 'FLAPS  0 · 10 · 20 · 30', style: 'label' },
  gear: { text: 'GEAR  UP — DN', style: 'label' },
  trim: { text: 'TRIM  NOSE UP', style: 'label' },
  breakers: { text: 'CIRCUIT BREAKERS', style: 'label' },
  fuelSel: { text: 'FUEL  L · BOTH · R', style: 'label' },
  noSmoking: { text: 'NO SMOKING', style: 'caution' },
  gearWarn: { text: 'CHECK GEAR DOWN', style: 'warning' },
  masterArm: { text: 'MASTER ARM', style: 'caution' },
  gun: { text: 'GUN', style: 'label' },
  missile: { text: 'MSL', style: 'label' },
  jettison: { text: 'JETTISON', style: 'warning' },
  eject: { text: 'EJECT', style: 'warning' },
  canopy: { text: 'CANOPY', style: 'label' },
  radar: { text: 'RADAR', style: 'label' },
  ecm: { text: 'ECM', style: 'label' },
});

const ORDER = Object.keys(PLACARDS) as PlacardId[];

/** Where a placard sits in the atlas, as a UV rectangle. */
export interface PlacardUv {
  u0: number;
  v0: number;
  u1: number;
  v1: number;
  /** The label's aspect ratio, so a quad can be sized without guessing. */
  aspect: number;
}

let atlasTexture: CanvasTexture | null = null;
const uvs = new Map<PlacardId, PlacardUv>();

function build(): void {
  const canvas = document.createElement('canvas');
  canvas.width = ATLAS;
  canvas.height = ATLAS;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('2D canvas context unavailable — placards cannot be drawn');

  // Transparent, not black: a placard quad sits *on* the panel and takes its surround from it, so
  // an opaque background would draw a black rectangle around every label.
  ctx.clearRect(0, 0, ATLAS, ATLAS);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  ORDER.forEach((id, index) => {
    const column = index % COLUMNS;
    const row = Math.floor(index / COLUMNS);
    if (row >= ROWS) return;
    const x = column * CELL_W;
    const y = row * CELL_H;
    const spec = PLACARDS[id];

    /*
     * The plate, then the text. Warning placards get a filled background because that is what makes
     * them a warning — a red *word* on a grey panel is just a word, and the thing a pilot's eye
     * catches across a cockpit is the block of colour.
     */
    if (spec.style !== 'label') {
      ctx.fillStyle = spec.style === 'warning' ? '#8d1410' : '#7a5c10';
      ctx.fillRect(x + 3, y + 4, CELL_W - 6, CELL_H - 8);
    }

    ctx.fillStyle = spec.style === 'label' ? '#d6dae0' : '#f2ece0';
    // Condensed and small: real placard lettering is narrow so it fits, and oversized text on a
    // 1 cm strip is the single fastest way to make an interior look like a toy.
    const size = spec.text.length > 14 ? 15 : 19;
    ctx.font = `600 ${size}px ui-monospace, "SF Mono", Menlo, monospace`;
    ctx.fillText(spec.text, x + CELL_W / 2, y + CELL_H / 2, CELL_W - 10);

    /*
     * `v` is flipped. `CanvasTexture` defaults to `flipY: true`, so the canvas row drawn at the top
     * is sampled at v = 1 — and a placard atlas indexed without accounting for that renders every
     * label upside down and belonging to the wrong switch, which is a very confusing thing to look
     * at and an easy thing to write.
     */
    uvs.set(id, {
      u0: x / ATLAS,
      u1: (x + CELL_W) / ATLAS,
      v0: 1 - (y + CELL_H) / ATLAS,
      v1: 1 - y / ATLAS,
      aspect: CELL_W / CELL_H,
    });
  });

  atlasTexture = new CanvasTexture(canvas);
  atlasTexture.colorSpace = SRGBColorSpace;
  atlasTexture.minFilter = LinearFilter;
  atlasTexture.magFilter = LinearFilter;
  atlasTexture.needsUpdate = true;
  atlasTexture.name = 'cockpit-placards';
}

/** The shared atlas, built on first use. */
export function placardAtlas(): CanvasTexture {
  if (atlasTexture === null) build();
  return atlasTexture as CanvasTexture;
}

/** Where a given placard lives in {@link placardAtlas}. */
export function placardUv(id: PlacardId): PlacardUv {
  if (atlasTexture === null) build();
  const uv = uvs.get(id);
  if (uv === undefined) throw new Error(`placard '${id}' has no atlas cell`);
  return uv;
}

/** Drop the atlas. For tests and for a full renderer teardown. */
export function disposePlacards(): void {
  atlasTexture?.dispose();
  atlasTexture = null;
  uvs.clear();
}
